import {
  ConnectionsViewResponseSchema,
  type ConnectionsViewResponseType,
  type SuggestionOverrideRevertBodyType,
  type SuggestionOverridesBodyType,
} from "@shared/schemas/connections.schemas";
import type {
  QueryClient,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { EventIdPublic } from "@shared/schemas/branded-id.schemas";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import { createConnectionsApi } from "./connections.api";

export const CONNECTIONS_QUERY_KEYS = {
  byEvent: (eventIdPublic: EventIdPublic) =>
    ["connections", eventIdPublic] as const,
} as const;

interface GetConnectionsViewOptions {
  readonly enabled?: boolean;
}

export class ConnectionsHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createConnectionsApi(apiClient);
  }

  createHooks() {
    return {
      useGetConnectionsView: (
        eventIdPublic: EventIdPublic,
        options: GetConnectionsViewOptions = {},
      ): UseQueryResult<ConnectionsViewResponseType> => {
        const { enabled = true } = options;

        return this.query({
          key: CONNECTIONS_QUERY_KEYS.byEvent(eventIdPublic),
          fn: () => this.api.getConnectionsView(eventIdPublic),
          parse: (response) => ConnectionsViewResponseSchema.parse(response),
          enabled: enabled && EventIdPublic.unwrap(eventIdPublic).length > 0,
          staleTime: 30 * 1000,
          retry: 1,
        });
      },

      useSaveConnectionOverrides: (): UseMutationResult<
        void,
        Error,
        {
          readonly eventIdPublic: EventIdPublic;
          readonly body: SuggestionOverridesBodyType;
        }
      > =>
        this.voidMutation({
          fn: async (input: {
            readonly eventIdPublic: EventIdPublic;
            readonly body: SuggestionOverridesBodyType;
          }) => {
            await this.api.saveConnectionOverrides(
              input.eventIdPublic,
              input.body,
            );
          },
          onSuccess: (_data, variables): void => {
            this.invalidateQueries(
              CONNECTIONS_QUERY_KEYS.byEvent(variables.eventIdPublic),
            );
          },
        }),

      useRevertConnectionOverrides: (): UseMutationResult<
        void,
        Error,
        {
          readonly eventIdPublic: EventIdPublic;
          readonly body: SuggestionOverrideRevertBodyType;
        }
      > =>
        this.voidMutation({
          fn: async (input: {
            readonly eventIdPublic: EventIdPublic;
            readonly body: SuggestionOverrideRevertBodyType;
          }) => {
            await this.api.revertConnectionOverrides(
              input.eventIdPublic,
              input.body,
            );
          },
          onSuccess: (_data, variables): void => {
            this.invalidateQueries(
              CONNECTIONS_QUERY_KEYS.byEvent(variables.eventIdPublic),
            );
          },
        }),
    };
  }
}
